import { DateTime } from 'luxon';
import memoizeOne from 'memoize-one';
import { cachedFromISO } from 'redux/selectors/utils/capacities';
import { fromISO } from 'utils/luxon-helpers';
import { orderBy } from 'utils/typesafe';
import type {
  GanttLaneSpecification,
  GanttLayoutMeta,
  GanttRowLayout,
  GanttTaskWithHorizontalPosition,
  GanttTaskWithLayout,
} from './gantt-internal-types';
import type { GanttRow, GanttTask } from './gantt-types';

const VERTICAL_SPACING = 8;
const VERTICAL_SPACE_BELOW_USER = 24;
const VERTICAL_SPACE_BELOW_PROJECT = 12;
export const USER_TASK_HEIGHT = 48;
export const USER_CAPACITY_ROW_HEIGHT = 14 + VERTICAL_SPACING;
export const USER_CAPACITY_ROW_TOP = VERTICAL_SPACING;
export const PROJECT_TASK_HEIGHT = 24;

const positionTopForLaneIndex = (
  index: number,
  type: 'user' | 'project',
  layoutOptions: CalculateLayoutOptions
) =>
  VERTICAL_SPACING +
  (type === 'user' && layoutOptions.showAvailability ? USER_CAPACITY_ROW_HEIGHT : 0) +
  index * ((type === 'user' ? USER_TASK_HEIGHT : PROJECT_TASK_HEIGHT) + VERTICAL_SPACING);
export const ESTIMATED_ROW_HEIGHT = positionTopForLaneIndex(2, 'user', { showAvailability: false });

export const calculateVisibleOffsets = (
  scrollLeft: number,
  width: number,
  layout: GanttLayoutMeta
) => {
  const offsetFromStart = Math.floor(scrollLeft / layout.zoom.columnWidth);
  const numVisibleDays = Math.ceil(width / layout.zoom.columnWidth);
  return { offsetFromStart, numVisibleDays };
};

export const calculateVisibleDays = (
  scrollLeft: number,
  width: number,
  layout: GanttLayoutMeta
) => {
  const { offsetFromStart, numVisibleDays } = calculateVisibleOffsets(scrollLeft, width, layout);
  const firstDate = fromISO(layout.startDate).plus({ days: offsetFromStart });
  const lastDate = firstDate.plus({ days: numVisibleDays });
  return { firstDate, lastDate } as const;
};

interface CalculateLayoutOptions {
  showAvailability: boolean;
}
export const calculateLayoutForRow = (
  row: GanttRow,
  layoutOptions: CalculateLayoutOptions
): GanttRowLayout => {
  const tasksWithOffsetPosition = row.tasks.map(task => positionTaskByOffset(task));
  const lanes = assignTasksToLanes(
    tasksWithOffsetPosition.filter(task => !task.task.hiddenByFilter)
  );

  const rowHeight =
    positionTopForLaneIndex(lanes.length, row.type, layoutOptions) +
    (row.type === 'user' ? VERTICAL_SPACE_BELOW_USER : VERTICAL_SPACE_BELOW_PROJECT);
  const tasks: Array<GanttTaskWithLayout> = [];
  lanes.forEach((lane, laneIndex) => {
    lane.forEach(task => {
      tasks.push({
        ...task,
        height: row.type === 'user' ? USER_TASK_HEIGHT : PROJECT_TASK_HEIGHT,
        top: positionTopForLaneIndex(laneIndex, row.type, layoutOptions),
      });
    });
  });

  const getOffsetFromLayoutMeta = memoizeOne(
    (meta: GanttLayoutMeta) => cachedFromISO(meta.startDate).diff(BASE_DATE_FOR_OFFSET, 'days').days
  );
  const getTasksPositioned = memoizeOne((meta: GanttLayoutMeta) => {
    // Since only this step requires the layout meta (which changes during zoom and infinite scroll),
    // we can bring in the layout meta as a parameter.
    const offsetFromLayoutMeta = getOffsetFromLayoutMeta(meta);
    return tasks.map(
      (task): GanttTaskWithLayout => ({
        task: task.task,
        top: task.top,
        height: task.height,
        left: (task.left - offsetFromLayoutMeta) * meta.zoom.columnWidth,
        width: task.width * meta.zoom.columnWidth - meta.zoom.dayPadding,
      })
    );
  });
  const getHiddenTasksHorizontallyPositioned = memoizeOne((meta: GanttLayoutMeta) => {
    const offsetFromLayoutMeta = getOffsetFromLayoutMeta(meta);
    return tasksWithOffsetPosition
      .filter(task => task.task.hiddenByFilter)
      .map(
        (task): GanttTaskWithLayout => ({
          task: task.task,
          top: 0,
          height: 0,
          left: (task.left - offsetFromLayoutMeta) * meta.zoom.columnWidth,
          width: task.width * meta.zoom.columnWidth - meta.zoom.dayPadding,
        })
      );
  });

  const laneSpecification: GanttLaneSpecification = {
    padTop: positionTopForLaneIndex(0, row.type, layoutOptions),
    laneHeight: row.type === 'user' ? USER_TASK_HEIGHT : PROJECT_TASK_HEIGHT,
    laneSpacing: VERTICAL_SPACING,
  };

  return {
    getTasks: getTasksPositioned,
    getHiddenTasks: getHiddenTasksHorizontallyPositioned,
    laneSpecification,
    rowHeight,
  };
};

const BASE_DATE_FOR_OFFSET = DateTime.now().startOf('day');
export const positionTaskByOffset = (task: GanttTask): GanttTaskWithHorizontalPosition => ({
  task,
  left: daysOffset(cachedFromISO(task.startDate), BASE_DATE_FOR_OFFSET),
  width: daysOffset(cachedFromISO(task.endDate), cachedFromISO(task.startDate)) + 1,
});

const assignTasksToLanes = (tasks: Array<GanttTaskWithHorizontalPosition>) => {
  const lanes: Array<Array<GanttTaskWithHorizontalPosition>> = [[]];

  orderBy(tasks, ['left', 'width'], ['asc', 'desc']).forEach(task => {
    lanes.some((lane, i) => {
      const isLastLane = i === lanes.length - 1;
      const hasOverlappingTask = lane.some(existingTask => {
        return horizontalOverlap(task, existingTask);
      });
      if (!hasOverlappingTask) {
        lane.push(task);
        return true;
      } else if (hasOverlappingTask && isLastLane) {
        const newLane = [task];
        lanes.push(newLane);
        return true;
      }
      return false;
    });
  });
  return lanes;
};

const daysOffsetCache = new WeakMap<[DateTime, DateTime], number>();

const daysOffset = (first: DateTime, second: DateTime) => {
  let diff = daysOffsetCache.get([first, second]);
  if (!diff) {
    diff = first.diff(second, 'days').days;
    daysOffsetCache.set([first, second], diff);
  }
  return diff;
};

export const horizontalOverlap = (
  a: { left: number; width: number },
  b: { left: number; width: number }
): boolean => {
  const aRight = a.left + a.width;
  const bRight = b.left + b.width;
  return !(aRight <= b.left || bRight <= a.left);
};
